import { Injectable } from "@angular/core";
import { Subject, BehaviorSubject } from "rxjs";
import * as localforage from "localforage";
import { jwtDecode } from 'jwt-decode';
import { environment } from "src/environments/environment";
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: "root",
})
export class LocalStorageService {
  public getLoggedInStatus = new Subject();
  public getLoggedInUsers = new Subject();
  private appearanceDetails = new BehaviorSubject(0);
  appearanceIds = this.appearanceDetails.asObservable();
  private applicantDetails = new BehaviorSubject(0);
  applicantIds = this.applicantDetails.asObservable();
  private encryptionKey = environment.JWT_ENCRYPTION_KEY // Must be 32 bytes


  setLocalStore(key, data) {
    return localStorage.setItem(key, JSON.stringify(data));
  }

  setLocalStorageItem(key, data) {
    return localStorage.setItem(key, data);
  }

  setAppearanceId(id: number) {
    this.appearanceDetails.next(id);
  }

  setApplicantId(id: number) {
    this.applicantDetails.next(id);
  }

  getLocalStorageItem(key) {
    const item = localStorage.getItem(key);
    if (item) {
      try {
        // Parse the item as JSON
        const parsedItem = JSON.parse(item);
  
        // Check and clean up businessId if necessary
        if (parsedItem.businessId && typeof parsedItem.businessId === 'string') {
          parsedItem.businessId = parsedItem.businessId.replace(/^"|"$/g, '');
        }
  
        return parsedItem;
      } catch (error) {
        console.error('Error parsing localStorage item:', error);
        return item; // Return raw value if parsing fails
      }
    }
    return item;
  }

  getLocalStore(key) {
    this.getLoggedInUsers.next(JSON.parse(localStorage.getItem(key)));
    return JSON.parse(localStorage.getItem(key));
  }

  clearStorageFor(key) {
    return localStorage.removeItem(key);
  }

  clearStorage() {
    return localStorage.clear();
  }

  isLoggedIn(key) {
    if (localStorage.getItem(key) === null) {
      this.getLoggedInStatus.next(localStorage.getItem(key) === null);
    }
    this.getLoggedInStatus.next(localStorage.getItem(key) !== null);
    return localStorage.getItem(key) !== null;
  }

  logout() {
    this.getLoggedInStatus.next(false);
    this.clearStorage();
  }

  async setDataInIndexedDB(key: string, value: any) {
    try {
      await localforage.setItem(key, JSON.stringify(value));
    } catch { }
  }

  // fetch particular key details
  async getDataFromIndexedDB(key: string) {
    return new Promise((resolve, reject) => {
      localforage
        .getItem(key)
        .then((result: any) => {
          resolve(JSON.parse(result));
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // For Remove Particular Field/Key
  removeDataFromIndexedDB(key: string) {
    return new Promise((resolve, reject) => {
      localforage
        .removeItem(key)
        .then(() => {
          const remove = "Key Removed";
          return resolve(remove);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  // Database has been entirely deleted.
  clearDataFromIndexedDB() {
    return localforage.clear();
  }

  async decodeLocalStorageToken() {
    const token = localStorage.getItem("token");
    const encryptedToken = token.split(" ")[1];
    const decryptedToken = await this.decryptToken(encryptedToken);
    var tokenValues: any = jwtDecode(decryptedToken);
    return tokenValues.role;
  }

  async decryptToken(encryptedToken) {
    const bytes = CryptoJS.AES.decrypt(encryptedToken, this.encryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  async isPermissionAllowed(key) {
    const token = localStorage.getItem("token");
    const encryptedToken = token.split(" ")[1];
    const decryptedToken = await this.decryptToken(encryptedToken);
    var tokenValues: any = jwtDecode(decryptedToken);
    const roles = tokenValues.role;
    if (roles[key] === true) {
      return true;
    } else {
      return false;
    }
  }

  getLocation(): Promise<{ latitude: number, longitude: number } | null> {
    const maxLocationAgeMs = 15 * 60 * 1000;
    return new Promise((resolve) => {
      const cachedLocation = this.getLocalStorageItem("lastKnownLocation");
      if (
        cachedLocation &&
        typeof cachedLocation === "object" &&
        typeof cachedLocation.latitude === "number" &&
        typeof cachedLocation.longitude === "number" &&
        typeof cachedLocation.capturedAt === "number" &&
        Date.now() - cachedLocation.capturedAt <= maxLocationAgeMs
      ) {
        resolve({
          latitude: cachedLocation.latitude,
          longitude: cachedLocation.longitude,
        });
        return;
      }

      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          const location = {
            latitude,
            longitude,
            capturedAt: Date.now(),
          };
          this.setLocalStore("lastKnownLocation", location);
          resolve({
            latitude,
            longitude,
          });
        },
        () => {
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000,
        }
      );
    });
  }
}
